import { SubscriberPackageDelimiter } from 'library/ts/model/user/SubscriberPackageDelimiter';
import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { SubscriberPackageValue } from 'library/ts/model/user/SubscriberPackageValue';
import { UserType } from 'library/ts/model/user/UserType';
import { TSubscriber } from 'library/ts/types/TSubscriber';

/**
 * @copyright © Paramount Streaming
 * @description Values used to compile a defined "user type"
 * @see {@link https://docs.google.com/spreadsheets/d/1xI0WfpsmMTFZfq40Zw9gcRcYgyW6arjXrLwMTY_VJfY/edit?gid=0#gid=0}
 */
export const UserTypeLibrary: { [key in keyof typeof UserType]: TSubscriber[] } = {

	/**
	 * @description Anonymous user
	 * Ad tier
	 * Region:
	 *	P+ Domestic anonymous
	 *
	 * @type {Array}
	 */
	[ UserType.ANON ]: [
		`${SubscriberPackageKey.ANON}${SubscriberPackageDelimiter.EMPTY}${SubscriberPackageValue.EMPTY}`,

		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_0}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_0}`
	],

	/**
	 * @description Limited Commercial user
	 * Ad tier
	 * Region:
	 *	P+ Domestic
	 *
	 * @type {Array}
	 */
	[ UserType.LC ]: [
		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_6}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_6}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_6}`
	],

	/**
	 * @description Commercial Free user
	 *
	 * Non Ad tier
	 * Region:
	 * 	Domestic Premium |
	 * 	Domestic Premium MVPD |
	 * 	International Standard |
	 * 	International Mobile Only
	 *
	 * @type {Array}
	 */
	[ UserType.CF ]: [
		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_14}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_14}`
	],

	/**
	 * @description Low Cost Plan user
	 *
	 * Ad tier
	 * Region:
	 * 	Domestic Essential |
	 * 	Domestic MVPD Essential |
	 * 	International Basic Ad Tier |
	 * 	International MVPD Ad Tier
	 *
	 * @type {Array}
	 */
	[ UserType.LCP ]: [
		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_22}`, // Domestic Essential LCP | AD Tier OR Intl Ad Plan / Basic (CA April 3rd 2024 , AU May 29th 2024) | Ad Tier
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_22}`, // Domestic Essential LCP | AD Tier OR Intl Ad Plan / Basic (CA April 3rd 2024 , AU May 29th 2024) | Ad Tier
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_22}`, // MVPD Essential (Charter users - from August 2024) | AD Tier

		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_23}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_23}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_23}` // P+ Intl MVPD Ad Tier | Ad Tier
	],

	/**
	 * @description MVPD Authenticated user
	 *
	 * Non Ad tier
	 * Region:
	 * 	Domestic Premium |
	 * 	Domestic Premium MVPD |
	 * 	International Standard |
	 * 	International Mobile Only |
	 * 	International MVPD Standard |
	 *	International Premium |
	 *	International MVPD Premium |
	 *	International MVPD Mobile Only
	 *
	 * @type {Array}
	 */
	[ UserType.MVPD_AUTHZ ]: [
		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_14}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_14}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_14}`, // MVPD Premium (Hulu, DirecTV,etc)  | NON Ad tier

		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_15}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_15}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_15}`, // MVPD Standard | NON Ad Tier

		// Intl Premium (released on CA, AU, BR, MX, Latam, France) | NON Ad Tier
		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_17}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_17}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_17}`,

		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_24}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_24}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_24}`, // MVPD Premium | NON Ad tier

		`${SubscriberPackageKey.SB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_25}`,
		`${SubscriberPackageKey.TSB}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_25}`,
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_25}` // MVPD Mobile Only | NON Ad Tier
	],

	/**
	 * @description MVPD Authenticated user from CBS TVE Domestic
	 *
	 * Ad tier
	 * Region:
	 * 	Domestic MVPD
	 *
	 * @type {Array}
	 */
	[ UserType.MVPD_AUTHZ_TVE ]: [
		`${SubscriberPackageKey.TVE}${SubscriberPackageDelimiter.V_PIPE}${SubscriberPackageValue.VALUE_18}` // MVPD and AuthZ | Ad Tier
	]
};